import type { ClosingChecklist, ClosingPersonalState } from '../components/closing/closing-model';
import {
  groupClosingProgress,
  groupStepProgress,
  leaseStepComplete,
  personalOrEmpty,
  personStepProgress,
  SHOW_HOUSEMATE_AGREEMENT_STEP,
  utilitySetupStepComplete,
  type ClosingGroupState,
  type ClosingMember,
} from '../components/closing/closing-model';
import type { ApplicationProgress } from './application-progress';

function toApplicationProgress(completed: number, total: number): ApplicationProgress {
  return {
    done: completed,
    total,
    percent: total <= 0 ? 0 : Math.round((completed / total) * 100),
  };
}

/** Per-person closing steps (PAD, void cheque, e-transfer) — same role as applicationProgress on Apply. */
export function closingPersonProgress(state: ClosingPersonalState): ApplicationProgress {
  const step = personStepProgress(state);
  return toApplicationProgress(step.completed, step.total);
}

/** Group closing bar: every person’s steps plus shared group steps (matches groupClosingProgress). */
export function closingGroupProgress(checklist: ClosingChecklist): ApplicationProgress {
  const raw = groupClosingProgress(checklist);
  return toApplicationProgress(raw.completed, raw.total);
}

export function closingProgressForPerson(
  personId: string,
  personal: Record<string, ClosingPersonalState>,
): ApplicationProgress {
  return closingPersonProgress(personalOrEmpty(personal, personId));
}

/** Viewer sidebar ring uses personal + group subset; pills use closingPersonProgress only. */
export function viewerClosingProgressForSidebar(
  checklist: ClosingChecklist | null | undefined,
): { completed: number; total: number } {
  if (!checklist?.selected) return { completed: 0, total: 1 };
  const viewer = checklist.members.find((member) => member.isViewer);
  const person = viewer
    ? personStepProgress(personalOrEmpty(checklist.personal, viewer.id))
    : { completed: 0, total: 3 };
  const group = groupStepProgress(checklist.members, checklist.group);
  return { completed: person.completed + group.completed, total: person.total + group.total };
}

export function preloadedClosingProgressByPersonId(
  members: ClosingMember[],
  personal: Record<string, ClosingPersonalState>,
): Record<string, ApplicationProgress> {
  return Object.fromEntries(
    members.map((member) => [member.id, closingProgressForPerson(member.id, personal)]),
  );
}

/** Group-level steps only (lease, utilities, and housemate when that card is shown). */
export function closingSharedGroupProgress(
  members: ClosingMember[],
  group: ClosingGroupState,
): ApplicationProgress {
  const leaseDone = leaseStepComplete(members, group);
  const completed = [
    leaseDone,
    utilitySetupStepComplete(group),
    SHOW_HOUSEMATE_AGREEMENT_STEP && Boolean(group.housemateSignedFileName),
  ].filter(Boolean).length;
  return toApplicationProgress(completed, SHOW_HOUSEMATE_AGREEMENT_STEP ? 3 : 2);
}
